import { useState, useMemo } from 'react';
import { Box, Button, Input, Heading, useToast, Text, Flex } from '@chakra-ui/react';
import axios from 'axios';

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const toast = useToast();

  const getPasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score, label: 'Too Short' };

    if (password.length >= 6) score += 1; // minimum length
    if (/[a-z]/.test(password)) score += 1; // lowercase
    if (/[A-Z]/.test(password)) score += 1; // uppercase
    if (/\d/.test(password)) score += 1; // number
    if (/[!@#$%^&*()_\-+=]/.test(password)) score += 1; // special char

    let label = 'Weak';
    if (score >= 4) label = 'Strong';
    else if (score >= 2) label = 'Medium';
    return { score, label };
  };

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const getStrengthColor = (score) => {
    switch (strength.label) {
      case 'Too Short': return 'gray.300';
      case 'Weak': return 'red.400';
      case 'Medium': return 'yellow.400';
      case 'Strong': return 'green.400';
      default: return 'gray.300';
    }
  };

  const handleChange = async () => {
    if (newPassword.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await axios.post(
        '/api/auth/change-password',
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast({
        title: 'Password changed successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={10} p={4} borderWidth={1} borderRadius="lg">
      <Heading size="md" mb={4}>Change Password</Heading>
      <Input
        placeholder="Current password"
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        mb={4}
      />
      <Input
        placeholder="New password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        mb={2}
      />

      {/* Password strength bar */}
      {newPassword.length > 0 && (
        <Box mb={4}>
          <Text fontSize="sm" mb={1}>Strength: {strength.label}</Text>
          <Flex h="6px" borderRadius="md" overflow="hidden" bg="gray.200">
            <Box
              w={`${(strength.score / 5) * 100}%`}
              bg={getStrengthColor(strength.score)}
              transition="width 0.3s"
            />
          </Flex>
        </Box>
      )}

      <Button colorScheme="blue" onClick={handleChange}>Change Password</Button>
    </Box>
  );
}
